import { useEffect, useState, type JSX } from 'react';
import type { Monitor, MonitorKind, MonitorState, NetworkNode } from '@netdeck/contracts';
import type { TFunction } from 'i18next';
import { currentLanguage, useTranslation } from '../../shared/i18n';
import { messageText } from '../../shared/i18n/message';
import { useErrorText } from '../../shared/i18n/useErrorText';
import { Button } from '../../shared/ui/Button';
import { formatAgo, formatMoment } from '../../shared/format/time';
import { EmptyState, Loading } from '../../shared/ui/Panel';
import { StatusDot, type Status } from '../../shared/ui/StatusDot';
import { MonitorForm, type MonitorFormValue } from './MonitorForm';
import { RttChart } from './RttChart';
import { readRttView, writeRttView, type RttView } from './rttPreferences';
import {
  useCheckNow,
  useCreateMonitor,
  useDeleteMonitor,
  useMonitors,
  useUpdateMonitor,
} from './api';

// Не экспортируется намеренно: файл с компонентом и обычным значением
// Vite не обновляет горячей заменой, а перезагружает целиком.
const MONITOR_KIND_LABELS: Record<MonitorKind, string> = {
  icmp: 'ICMP',
  tcp: 'TCP',
  http: 'HTTP',
  snmp: 'SNMP',
};

/**
 * Мониторы выбранного узла.
 *
 * Живой статус берётся из подписки, а не из ответа `GET /monitors`: между
 * запросами состояние успевает поменяться, и показывать записанное в БД
 * в момент загрузки страницы значит показывать прошлое.
 */
export function MonitorsSection({
  node,
  live,
  canWrite,
}: {
  node: NetworkNode;
  live: Map<string, MonitorState>;
  canWrite: boolean;
}): JSX.Element {
  const monitors = useMonitors(node.id);
  const createMonitor = useCreateMonitor(node.id);
  const updateMonitor = useUpdateMonitor(node.id);
  const deleteMonitor = useDeleteMonitor(node.id);
  const checkNow = useCheckNow();

  const { t } = useTranslation();

  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const now = useTicker();

  /*
    Вид графиков общий на всю секцию: у одного узла до десятка мониторов,
    и переключать каждый по отдельности — работа на ровном месте.
  */
  const [rttView, setRttView] = useState<RttView>(readRttView);

  const changeRttView = (next: RttView): void => {
    setRttView(next);
    writeRttView(next);
  };

  const rows = monitors.data ?? [];

  return (
    <section className="flex min-h-0 flex-col">
      <header className="flex h-9 flex-none items-center gap-2.5 border-b border-line bg-row-alt px-3.5">
        <h4 className="text-base font-medium">
          {t('monitors.title')} · {rows.length}
        </h4>
        {canWrite ? (
          <Button
            icon={adding ? 'close' : 'plus'}
            size="sm"
            className="ml-auto"
            onClick={() => {
              setEditingId(null);
              setAdding((value) => !value);
            }}
          >
            {adding ? t('common.action.cancel') : t('common.action.add')}
          </Button>
        ) : null}
      </header>

      {adding ? (
        <MonitorForm
          mode="create"
          nodeHasAddress={node.address !== null}
          pending={createMonitor.isPending}
          error={createMonitor.error}
          onCancel={() => setAdding(false)}
          onSubmit={(value) =>
            createMonitor.mutate(toCreateInput(value), { onSuccess: () => setAdding(false) })
          }
        />
      ) : null}

      {monitors.isPending ? (
        <Loading />
      ) : rows.length === 0 ? (
        <EmptyState
          title={t('monitors.emptyTitle')}
          hint={t('monitors.emptyHint')}
          action={
            canWrite ? (
              <Button icon="plus" size="sm" onClick={() => setAdding(true)}>
                {t('monitors.add')}
              </Button>
            ) : null
          }
        />
      ) : (
        <ul className="flex flex-col">
          {rows.map((monitor) =>
            editingId === monitor.id ? (
              <li key={monitor.id} className="border-b border-row-line">
                <MonitorForm
                  mode="edit"
                  monitor={monitor}
                  nodeHasAddress={node.address !== null}
                  pending={updateMonitor.isPending}
                  error={updateMonitor.error}
                  onCancel={() => setEditingId(null)}
                  onSubmit={(value) =>
                    updateMonitor.mutate(
                      { monitorId: monitor.id, input: toUpdateInput(value) },
                      { onSuccess: () => setEditingId(null) },
                    )
                  }
                />
              </li>
            ) : (
              <MonitorRow
                key={monitor.id}
                monitor={monitor}
                live={live}
                canWrite={canWrite}
                checkNow={checkNow}
                busy={updateMonitor.isPending || deleteMonitor.isPending}
                now={now}
                rttView={rttView}
                onRttViewChange={changeRttView}
                onEdit={() => {
                  setAdding(false);
                  setEditingId(monitor.id);
                }}
                onToggle={() =>
                  updateMonitor.mutate({
                    monitorId: monitor.id,
                    input: { isEnabled: !monitor.isEnabled },
                  })
                }
                onDelete={() => {
                  if (confirm(t('monitors.deleteConfirm'))) {
                    deleteMonitor.mutate(monitor.id);
                  }
                }}
              />
            ),
          )}
        </ul>
      )}
    </section>
  );
}

function MonitorRow({
  monitor,
  live,
  canWrite,
  checkNow,
  busy,
  now,
  rttView,
  onRttViewChange,
  onEdit,
  onToggle,
  onDelete,
}: {
  monitor: Monitor;
  live: Map<string, MonitorState>;
  canWrite: boolean;
  checkNow: ReturnType<typeof useCheckNow>;
  busy: boolean;
  now: number;
  /** Вид графика — общий на все мониторы узла. */
  rttView: RttView;
  onRttViewChange: (view: RttView) => void;
  onEdit: () => void;
  onToggle: () => void;
  onDelete: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const errorText = useErrorText();
  const language = currentLanguage();

  const mine = checkNow.variables === monitor.id;
  const current = freshest(monitor, live);

  return (
    <li className="flex flex-col gap-1.5 border-b border-row-line px-3.5 py-2.5">
      <div className="flex items-center gap-2">
        <StatusDot status={current.status} />
        <span className="font-mono text-sm">
          {MONITOR_KIND_LABELS[monitor.kind]}
          {monitor.port === null ? '' : `:${monitor.port}`}
        </span>
        <span className="truncate font-mono text-xs text-dim">{monitor.target}</span>
        <span className="ml-auto font-mono text-xs text-muted">{formatRtt(current.rttMs, t)}</span>
      </div>

      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 font-mono text-2xs text-dim">
        <span>{t('monitors.everySeconds', { seconds: monitor.intervalSec })}</span>
        <span>·</span>
        <span>{t('monitors.timeoutMs', { ms: monitor.timeoutMs })}</span>
        <span>·</span>
        <span>
          {t('monitors.thresholds', {
            fail: monitor.failThreshold,
            ok: monitor.okThreshold,
          })}
        </span>
        {monitor.isEnabled ? null : <span className="text-warn">{t('monitors.off')}</span>}
      </div>

      <RttChart
        monitorId={monitor.id}
        everChecked={monitor.lastCheckedAt !== null}
        view={rttView}
        onViewChange={onRttViewChange}
      />

      <LastCheck monitor={monitor} checkedAt={current.checkedAt} now={now} />

      {monitor.currentSince ? (
        <div className="font-mono text-2xs text-faint">
          {t('monitors.inStateSince', { at: formatMoment(monitor.currentSince, language) })}
        </div>
      ) : null}

      {monitor.lastError ? (
        <div className="font-mono text-xs text-down-fg">{messageText(monitor.lastError, t)}</div>
      ) : null}

      {mine && checkNow.error ? (
        <div className="text-sm text-down-fg">{errorText(checkNow.error)}</div>
      ) : null}

      {mine && checkNow.isSuccess ? (
        <div className="text-sm text-muted">{t('monitors.checkRequested')}</div>
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        <Button
          icon="refresh"
          size="sm"
          variant="ghost"
          disabled={!monitor.isEnabled || checkNow.isPending}
          title={monitor.isEnabled ? t('monitors.checkNowHint') : t('monitors.checkNowDisabled')}
          onClick={() => checkNow.mutate(monitor.id)}
        >
          {t('common.action.refresh')}
        </Button>

        {canWrite ? (
          <>
            <Button icon="pencil" size="sm" variant="ghost" onClick={onEdit} disabled={busy}>
              {t('monitors.edit')}
            </Button>
            <Button icon="power" size="sm" variant="ghost" onClick={onToggle} disabled={busy}>
              {monitor.isEnabled ? t('monitors.disable') : t('monitors.enable')}
            </Button>
            <Button icon="trash" size="sm" variant="danger" onClick={onDelete} disabled={busy}>
              {t('common.action.delete')}
            </Button>
          </>
        ) : null}
      </div>
    </li>
  );
}

function toCreateInput(value: MonitorFormValue) {
  return {
    kind: value.kind,
    ...(value.target ? { target: value.target } : {}),
    ...(value.port === null ? {} : { port: value.port }),
    intervalSec: value.intervalSec,
    timeoutMs: value.timeoutMs,
    failThreshold: value.failThreshold,
    okThreshold: value.okThreshold,
    isEnabled: true,
    ...(value.config ? { config: value.config } : {}),
  };
}

/**
 * При изменении цель отправляется всегда, в том числе пустая: пустая строка
 * здесь означает «вернуться к адресу узла», и молча оставить прежнее значение
 * было бы не тем, что просил пользователь.
 */
function toUpdateInput(value: MonitorFormValue) {
  return {
    target: value.target,
    port: value.port,
    intervalSec: value.intervalSec,
    timeoutMs: value.timeoutMs,
    failThreshold: value.failThreshold,
    okThreshold: value.okThreshold,
    ...(value.config ? { config: value.config } : {}),
  };
}

/**
 * Статус монитора: из подписки, если она принесла его, иначе из БД.
 *
 * Второе — не дубль, а честное «что известно»: до первого снимка показывать
 * серое у монитора, который давно up, значит врать в другую сторону.
 */
/**
 * Часы для строки «проверен N назад».
 *
 * Без тика надпись замерла бы: перерисовка происходит по приходу телеметрии,
 * а её отсутствие — ровно тот случай, который надо показать. Раз в десять
 * секунд достаточно: точность здесь никому не нужна, а нужен сам факт,
 * что число растёт.
 */
function useTicker(): number {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 10_000);
    return () => clearInterval(timer);
  }, []);

  return now;
}

/**
 * Когда монитор проверялся в последний раз.
 *
 * Отдельная строка, а не приписка к статусу: она отвечает на другой вопрос.
 * Статус говорит, что было, а это — работает ли проверка вообще. «Up»,
 * измеренный полчаса назад, выглядит на экране ровно как «up» секунду назад,
 * и без этой строки остановившегося агента видно только по ленте событий,
 * где ничего не происходит.
 */
function LastCheck({
  monitor,
  checkedAt,
  now,
}: {
  monitor: Monitor;
  checkedAt: string | null;
  now: number;
}): JSX.Element {
  const { t } = useTranslation();
  const language = currentLanguage();

  if (!checkedAt) {
    return (
      <div className="font-mono text-2xs text-faint">
        {monitor.isEnabled ? t('monitors.noChecksYet') : t('monitors.offNoChecks')}
      </div>
    );
  }

  const ageSec = Math.max(0, Math.round((now - new Date(checkedAt).getTime()) / 1000));

  // Просрочка втрое против интервала — это уже не сетевая задержка,
  // а остановившийся агент или расхождение часов на площадке.
  const overdue = monitor.isEnabled && ageSec > monitor.intervalSec * 3 + 10;

  return (
    <div className={`font-mono text-2xs ${overdue ? 'text-warn' : 'text-faint'}`}>
      {t('monitors.checkedAgo', {
        ago: formatAgo(ageSec, t),
        at: formatMoment(checkedAt, language),
      })}
      {overdue ? ` · ${t('monitors.overdue')}` : ''}
    </div>
  );
}

/**
 * Что известно о мониторе прямо сейчас. Источников два, и они дополняют
 * друг друга, а не дублируют:
 *
 *  - подписка приносит **смены статуса** мгновенно, но молчит, пока статус
 *    не меняется, то есть на исправно работающем узле — всё время;
 *  - опрос приносит время последней проверки и свежий отклик, но с шагом
 *    в двадцать секунд.
 *
 * Статус берётся из подписки: между опросами она единственная знает
 * о падении. Время и отклик — из того источника, который новее; сравнение
 * идёт по меткам, а не по предпочтению одного из них, иначе после опроса
 * на экране оставался бы отклик, измеренный при последней смене статуса,
 * то есть сколь угодно давно.
 */
function freshest(
  monitor: Monitor,
  live: Map<string, MonitorState>,
): { status: Status; rttMs: number | null; checkedAt: string | null } {
  const state = live.get(monitor.id);
  const status = toStatus(state?.status ?? monitor.currentStatus);

  const liveAt = state?.since ?? null;
  const rowAt = monitor.lastCheckedAt;
  const liveIsNewer = liveAt !== null && (rowAt === null || liveAt > rowAt);

  return liveIsNewer
    ? { status, rttMs: state?.rttMs ?? null, checkedAt: liveAt }
    : { status, rttMs: monitor.lastRttMs, checkedAt: rowAt };
}

function toStatus(status: string): Status {
  return status === 'up' ? 'up' : status === 'down' ? 'down' : 'unknown';
}

function formatRtt(rttMs: number | null, t: TFunction): string {
  return rttMs === null ? t('common.dash') : t('monitors.rtt', { value: rttMs.toFixed(1) });
}
